/**
 * FleetEditor — ADR-111 IA amendment (agent-centric, not file-centric,
 * accepted 2026-07-28): the Fleet Editor's shell. A left Agents rail (IA-1)
 * replaces both the old literal Files tree and the separate Agent panel;
 * selecting an agent shows its facets, each embedding an existing abstract
 * editor scoped to that agent (IA-2) — Persona, Skills, Workflows, Crons,
 * Capabilities/MCP (read-only), Profile, and — for the brain — Routing
 * (IA-5). A "Resources & files" entry pinned at the rail's bottom is the
 * IA-3 escape hatch (FilesStudio, unscoped, for the long tail).
 *
 * The routes (IA-6) that render this component are declared in ONE place,
 * `fleetEditorRoutes.tsx`, so the app and the shell contract tests mount the
 * same table — read them there rather than from a second list here. Among them
 * is `/fleet-editor/:agent/:name`, the pre-amendment D2 shape, compat-redirected
 * to the workflows deep-link shape by LegacyWorkflowRedirect below.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, type AgentCapabilities, type CapabilityCatalogEntry, type CapabilityProposeResult } from '../api/client'
import { registrationTone } from '../components/ProposeResultBanner'
import { useHeaderSlot } from '../context/HeaderSlotContext'
import { useAuth } from '../context/AuthContext'
import { WorkflowsFacet } from './Workflows'
import { SkillStudio } from './SkillStudio'
import { CronStudio } from './CronStudio'
import { FilesStudio } from './FilesStudio'
import { agentFilesScope } from './resourceTree'
import { DataArea } from './DataArea'
import { PlatformSkillsArea } from './PlatformSkillsArea'
import { PersonaFacet } from './PersonaFacet'
import { RoutingFacet } from './RoutingFacet'
import { NewAgentForm, NewAgentProposeStrip, type PendingPropose } from './NewAgentArea'
import { AgentProfileFacet } from './AgentProfileFacet'
import { isPrefixStaged, useStagedPaths } from '../hooks/useStagedPaths'
import { useResizableWidth } from '../hooks/useResizableWidth'
import { StagedBadge } from '../components/StagedBadge'
import { OverflowNav } from '../components/OverflowNav'
import { type Facet, isBrain, railFacetFor } from './fleetEditorFacets'
import { dataSetFromPath, fleetAreaFor, NEW_AGENT_PATH } from './fleetEditorArea'
import { PLATFORM_SKILLS_PATH, platformSkillFromPath } from './platformSkills'

const BASE_FACETS: { id: Facet; label: string }[] = [
  { id: 'persona', label: 'Persona' },
  { id: 'skills', label: 'Skills' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'crons', label: 'Crons' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'profile', label: 'Profile' },
  // ADR-111 IA-7 — LAST, deliberately. The typed facets above stay primary
  // (IA-2); this is the agent-scoped long tail no typed editor owns.
  { id: 'files', label: 'Files' },
]

// The facet tab's box, split out so the live Link and the width-measuring mirror
// OverflowNav renders for it are guaranteed to carry the same classes. A mirror
// that loses the padding or the font size measures narrower than the tab it
// stands for, which under-hides and lets the header's clip cut a real tab.
const FACET_TAB_CLASS = 'px-2.5 py-1 rounded text-sm font-medium transition-colors'
const FACET_TAB_ACTIVE_CLASS = 'bg-indigo-50 text-indigo-700'
const FACET_TAB_IDLE_CLASS = 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'

/** The Agents rail's width. The default is the old fixed `w-56`; the rail's
 *  edge drags because descriptive agent names (`source-analyst-…`) truncated
 *  at that width with the staged/core badges beside them. */
const DEFAULT_RAIL_PX = 224
const MIN_RAIL_PX = 160
const MAX_RAIL_PX = 480
/** What the facet beside the rail keeps however wide the rail is dragged. */
const MIN_MAIN_PX = 560


/** Capabilities/MCP facet — ADR-111 D4/D5 addendum (2026-08-21), PHASE 1.
 *
 * Until 2026-08-21 this rendered a placeholder sentence and a link to Console,
 * on D5's reasoning that enablement lives there. The addendum re-cut that
 * boundary along supply vs binding: Console keeps the catalog, the
 * prerequisites/credentials behind it, and admin-config with its provisioning
 * fan-out; per-agent BINDING becomes MC's, because which capabilities an agent
 * has is an agent characteristic belonging beside its SOUL, skills and model.
 *
 * Phase 1 made the facet HONEST — it renders the agent's real bound servers from
 * GET /api/agents/:agent/capabilities. The placeholder's stated reason ("MC has
 * no endpoint exposing an agent's enabled MCP servers") was true of the endpoint
 * and false of the data: copilot has joined the same two files since ADR-079 D3.
 *
 * Phase 3 made it WRITABLE. Enable/disable propose a reviewable changeset on the
 * same pipeline as every other Fleet Editor write. Two things about that are
 * easy to misread and are stated in the UI rather than left implicit:
 *
 *   - the list shows what is DEPLOYED, so a successful propose does NOT move it.
 *     Without saying so, a correct enable looks like it silently failed.
 *   - only self-serve capabilities are offered. admin_only ones are superadmin-
 *     gated in Console and the propose would 409, so offering them here would be
 *     an affordance that cannot succeed.
 *
 * The catalog, credentials and admin-config stay Console's (D5.4) — hence the
 * Console link, which is now for the half that genuinely did not move. */
function CapabilitiesFacet({ agent, canPropose }: { agent: string; canPropose: boolean }) {
  const [consoleURL, setConsoleURL] = useState<string | null>(null)
  const [fleetSlug, setFleetSlug] = useState('')
  const [data, setData] = useState<AgentCapabilities | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  const [catalog, setCatalog] = useState<CapabilityCatalogEntry[]>([])
  const [picking, setPicking] = useState(false)
  // Tools are COLLAPSED by default, per capability. A busy agent mounts several
  // servers with a dozen tools each, and expanding them all pushed the actual
  // subject of the page — which capabilities this agent has — off the screen.
  // Keyed by capability so opening one does not open the rest.
  const [toolsOpen, setToolsOpen] = useState<Record<string, boolean>>({})
  const [busy, setBusy] = useState('')
  const [result, setResult] = useState<CapabilityProposeResult | null>(null)
  const [actionError, setActionError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    api.getConfig()
      .then(c => { setConsoleURL(c.console_url); setFleetSlug(c.fleet_slug ?? '') })
      .catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.getAgentCapabilities(agent)
      .then(d => { if (!cancelled) { setData(d); setLoading(false) } })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load capabilities')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [agent, reloadKey])

  // The catalog is only needed to OFFER an enable, so it is fetched only for a
  // fleet_owner. A viewer seeing a picker they cannot use is worse than not
  // seeing one.
  useEffect(() => {
    if (!canPropose) return
    let cancelled = false
    api.listCapabilityCatalog()
      .then(r => { if (!cancelled) setCatalog(r.capabilities ?? []) })
      .catch(() => { /* the picker just stays empty; the list below is unaffected */ })
    return () => { cancelled = true }
  }, [canPropose])

  // Dismiss the Add-capability menu on an outside click or Escape.
  //
  // The ref wraps the TOGGLE BUTTON as well as the menu, deliberately: with the
  // button outside it, clicking the button while open counts as an outside
  // click, the listener closes the menu, and the button's own onClick then
  // reopens it — so the menu never appears to close and the toggle looks dead.
  const pickerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!picking) return
    function onPointerDown(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPicking(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPicking(false)
    }
    // mousedown, not click: closing on press matches every other menu, and a
    // click listener would still be armed if the press started inside and the
    // release landed outside.
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [picking])

  const caps = data?.capabilities ?? []
  const boundKeys = new Set(caps.map(c => c.key))

  // Only self-serve capabilities are offered. admin_only / request-availability
  // ones are gated on superadmin in Console, and the propose would come back 409
  // — offering them here would be an affordance that cannot succeed.
  const offerable = catalog
    .filter(e => !boundKeys.has(e.key))
    .filter(e => !e.availability || e.availability === 'self_serve')

  async function act(capability: string, action: 'enable' | 'disable') {
    setBusy(`${action}:${capability}`)
    setActionError('')
    setResult(null)
    try {
      const r = await api.proposeCapability(agent, { capability, action })
      setResult(r)
      setPicking(false)
      // Re-read: an enable that landed as a PR does NOT change the deployed
      // fleet.yaml this facet reads, so the list will look unchanged. That is
      // correct, and the result banner is what tells the operator a PR is open.
      setReloadKey(k => k + 1)
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : `Failed to ${action} ${capability}`)
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Capabilities</h1>
        <p className="text-sm text-gray-500 mt-1">
          The MCP servers {agent} mounts, and the tools each one gives it. Enabling and disabling
          propose a reviewable change, like every other edit here. The list shows what is
          <span className="font-medium"> deployed</span>, so it only moves once a change is approved
          and deployed. The capability catalog and its credentials stay in Console.
        </p>
      </div>

      {canPropose && (
        <div className="relative" ref={pickerRef}>
          <button
            onClick={() => { setPicking(p => !p); setActionError('') }}
            className="text-sm px-3 py-1.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            ＋ Add capability
          </button>
          {picking && (
            <div className="absolute left-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded shadow-lg min-w-[320px] max-h-80 overflow-auto">
              {offerable.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-500 italic">
                  {catalog.length === 0
                    ? 'The capability catalog could not be read.'
                    : `${agent} already has every self-serve capability. To re-wire one whose ` +
                      `credential arrived later, use Fix wiring on its row below — no need to disable it.`}
                </p>
              ) : offerable.map(e => (
                <button
                  key={e.key}
                  onClick={() => act(e.key, 'enable')}
                  disabled={busy !== ''}
                  className="w-full text-left px-3 py-2 hover:bg-indigo-50 disabled:opacity-40 border-b border-gray-100 last:border-0"
                >
                  <span className="text-xs font-medium text-gray-900">{e.label}</span>
                  {e.requires_prerequisite && (
                    <span className="ml-2 text-xs text-amber-700">needs {e.requires_prerequisite}</span>
                  )}
                  {e.summary && <span className="block text-xs text-gray-500 mt-0.5">{e.summary}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{actionError}</div>
      )}

      {/* A propose opens a PR; it does NOT change what this facet reads (the
          DEPLOYED fleet.yaml). Saying so explicitly stops the list looking broken
          when it correctly does not move. */}
      {result && (
        <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-sm text-indigo-900 space-y-1">
          {result.changed === false ? (
            <p>{result.detail || 'Already in that state — nothing to propose.'}</p>
          ) : (
            <>
              {/* Review happens IN Mission Control, and a failed registration is not an
                  invitation to go elsewhere. Three outcomes, and the operator is told only what
                  the server actually established:

                  - registration WARNED: the warning alone. The pull request is open, but no row
                    is proven to exist, so "sent for review" and any route to a review page are
                    both claims that have not been made. There is genuinely nowhere in-app to send
                    them -- and github.com is not the answer, because ADR-079 D5 states owners are
                    not expected to have, or need, GitHub access, so "approve it on GitHub" is
                    advice most of them cannot act on. The server's text says it needs technical
                    help, which is true and actionable.
                  - registered WITH an id: the Approve & Deploy page for that row.
                  - registered with NO id: the Improvements list, which does contain it. */}
              {result.improvement_warning ? (
                <div className={`rounded border px-3 py-2 text-xs ${registrationTone(result.registration_state)}`}>
                  <p>{result.improvement_warning}</p>
                </div>
              ) : (
                <p>
                  Sent for review. Nothing changes until you approve it.{' '}
                  {result.improvement_id
                    ? <Link to={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
                    : <>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</>}
                  {' '}The list below still shows what is DEPLOYED — it updates once the change is approved and deployed.
                </p>
              )}
              {result.prereq_warning && <p className="text-amber-800">{result.prereq_warning}</p>}
              {(result.manual_wiring ?? []).length > 0 && (
                <div>
                  <p className="font-medium">Still needs manual credential wiring:</p>
                  <ul className="list-disc ml-5">
                    {(result.manual_wiring ?? []).map(n => (
                      <li key={n.env_var}><span className="font-mono">{n.env_var}</span> — {n.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading capabilities…</p>}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>
      )}

      {/* A catalog that could not be READ is said out loud. Rendering key-only
          entries silently would look like a set of capabilities that happen to
          have no tools, which is a different and wrong claim. */}
      {data && !data.catalog_readable && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
          The MCP catalog could not be read, so names, tools and risk levels are unavailable below.
          The bindings themselves are accurate.
        </div>
      )}

      {/* Said once, not per row: the console could not be asked about credentials
          at all. Distinct from any single capability's state.

          Gated on BOTH reads failing. They hit different Console routes and
          either can fail alone, so showing this whenever the fleet-level read
          fails would claim nothing is verified while the per-agent rows below
          are sitting there verified — which teaches operators to ignore the
          banner. */}
      {data && !data.prerequisites_readable && !data.credentials_readable && (
        <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-600">
          Credential status couldn&apos;t be checked right now — Console didn&apos;t answer. The capabilities
          below are accurate; only their credential state is unverified.
        </div>
      )}

      {data?.core_owned && (
        <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-600">
          {agent} is a core-owned agent (ADR-103). Its capabilities are provided by core and are not
          editable per fleet.
        </div>
      )}

      {/* The empty state told every operator that enabling from here "is coming
          with phase 3 of the migration" and to go to Console instead — while the
          ＋ Add capability button sat directly above it, because phase 3c
          shipped and this copy did not move with it. A fleet owner was offered
          the control and told in the same breath that it did not exist yet,
          which is worse than either message alone: it makes the button look
          broken, so the one person who could use it does not.

          Suppressed entirely for a core-owned agent — the banner above already
          explains that its capabilities come from core, and a second grey box
          telling the operator to add one would contradict it.

          For an owner it also names the DEPLOYED-vs-proposed nuance, which is
          the next thing that confuses someone here: an enable lands as a PR, so
          this list is still empty afterwards and correctly so. Without that
          sentence a successful propose looks like it did nothing. */}
      {data && caps.length === 0 && !data.core_owned && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-500 italic">
          {canPropose ? (
            <>
              {agent} has no MCP capabilities deployed yet. Use{' '}
              <span className="font-medium not-italic">＋ Add capability</span> above to propose one —
              it lands as a reviewable PR, so this list stays empty until that change is approved and
              deployed.
            </>
          ) : (
            <>{agent} has no MCP capabilities deployed yet. A fleet owner can add one from this page.</>
          )}
        </div>
      )}

      <div className="space-y-3">
        {caps.map(cap => (
          <div key={cap.key} className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {cap.label}
                  {cap.label !== cap.key && (
                    <span className="ml-2 text-xs font-mono text-gray-400">{cap.key}</span>
                  )}
                </p>
                {cap.summary && <p className="text-xs text-gray-500 mt-0.5">{cap.summary}</p>}
              </div>
              <div className="shrink-0 flex flex-wrap gap-1 justify-end">
                {cap.access && <Badge tone={cap.access === 'read_write' ? 'amber' : 'blue'}>{cap.access}</Badge>}
                {cap.risk && <Badge tone={cap.risk === 'high' ? 'red' : 'gray'}>risk: {cap.risk}</Badge>}
                {cap.tier && <Badge tone="gray">{cap.tier}</Badge>}
                {canPropose && !data?.core_owned && (
                  <button
                    onClick={() => act(cap.key, 'disable')}
                    disabled={busy !== ''}
                    title={`Propose removing ${cap.label} from ${agent}`}
                    className="text-xs px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:border-red-300 hover:text-red-700 disabled:opacity-40"
                  >
                    {busy === `disable:${cap.key}` ? '…' : 'Disable'}
                  </button>
                )}
              </div>
            </div>

            {/* Drift: the fleet mounts a server the catalog does not describe.
                Shown rather than hidden — hiding it would under-report what the
                agent actually runs with. */}
            {!cap.in_catalog && (
              <p className="mt-2 text-xs text-amber-700">
                Not in the MCP catalog — the fleet mounts this server but the platform manifest does
                not describe it. Metadata below is unavailable, not empty.
              </p>
            )}

            {/* PER-AGENT credential standing, preferred over the fleet-level
                prerequisite_state below whenever the console answered.

                WHY IT REPLACES THE FLEET-LEVEL LINE. prerequisite_state answers
                "is this credential set up for the FLEET". On orion that was
                green for hubspot_app — truthfully, five agents use HubSpot —
                while the 1Password item a sixth agent's binding named did not
                exist. This row therefore showed "✓ provisioned" beside an
                absent credential, and the absence surfaced only when `op inject`
                aborted the deploy for every profile in the fleet. A per-agent
                credential needs a per-agent answer.

                Each row carries the three things that make it actionable: WHICH
                location (op_ref), whether it is this agent's alone or shared,
                and a link to the Console page that provisions it.

                `?? []` is not defensive noise: during a rolling deploy this
                frontend can be served a response from a backend that predates
                the field, and `.length` on undefined blanks the whole facet
                behind an error boundary — losing the bindings too, which are
                the part that was never in doubt. */}
            {(cap.credentials ?? []).length > 0 && (
              <ul className="mt-2 space-y-1">
                {(cap.credentials ?? []).filter(cr => cr.state !== 'not_required').map(cr => (
                  <li key={`${cr.capability}:${cr.env_var}`} className="text-xs">
                    <span className={
                      cr.state === 'satisfied' && cr.wired ? 'text-green-700'
                        : cr.state === 'satisfied' ? 'text-amber-800'
                        : cr.state === 'missing' ? 'text-amber-800' : 'text-gray-500'
                    }>
                      {cr.state === 'satisfied' && cr.wired && '✓ '}
                      {cr.state === 'satisfied' && !cr.wired && '⚠ '}
                      {cr.state === 'missing' && '⚠ '}
                      <span className="font-mono">{cr.env_var}</span>
                      {cr.state === 'satisfied' && (cr.wired
                        ? ' is provisioned'
                        // PROVISIONED IS NOT THE SAME AS WORKING. The credential
                        // exists and the binding does not reference it, so the
                        // capability is deployed with no token. Rendering a plain
                        // green "is provisioned" here is what hid that on
                        // crm-operator.
                        : ' exists, but this capability isn’t using it yet')}
                      {/* The run-time consequence belongs HERE and only here.
                          The old fleet-level line made this claim from the
                          fleet's bit, which orion showed can be red while THIS
                          agent's credential exists — a false alarm. Once the
                          vault has answered about this agent's own ref, the
                          claim is true. */}
                      {cr.state === 'missing' &&
                        ' is not provisioned — this capability will fail at run time until it is set up'}
                      {cr.state === 'unknown' && ' — status couldn’t be checked just now'}
                    </span>
                    {/* Scope is not decoration: it is the blast radius of
                        revoking or rotating this credential. */}
                    <span className="ml-1.5 text-gray-400">
                      {cr.per_agent ? `(${agent} only)` : '(shared by the fleet)'}
                    </span>
                    {/* The LOCATION. "Not provisioned" without saying where is a
                        dead end — this is the difference between a warning and
                        an instruction. An op:// ref is a location, not a secret,
                        and is already committed in the fleet's op.env.tpl. */}
                    {cr.op_ref && (
                      <span className="block text-gray-400 font-mono truncate" title={cr.op_ref}>
                        {cr.op_ref}
                      </span>
                    )}
                    {/* Provisioning is Console's (ADR-111 D5.4), so this links
                        out rather than offering an input MC must not own. The
                        path is CONSOLE'S OWN — it builds it and we render it
                        against the configured base URL, so the link cannot go
                        stale the next time that page moves. */}
                    {consoleURL && cr.console_path && cr.state === 'missing' && (
                      <a
                        href={`${consoleURL}${cr.console_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-700 underline"
                      >
                        {cr.prerequisite_label
                          ? `Set up ${cr.prerequisite_label} in Console →`
                          : 'Set it up in Console →'}
                      </a>
                    )}
                    {/* THE REPAIR, AS ONE BUTTON.
                        
                        The instruction used to say "re-run Add capability",
                        which is a trap: that picker deliberately omits a
                        capability the agent already has, so following it means
                        Disable first — two proposals, two approvals, and a
                        window where the capability is genuinely removed from a
                        working agent.
                        
                        None of that is needed. Re-proposing the SAME enable
                        produces a change touching only op.env.tpl, because
                        fleet.yaml already has the mount. So this button calls
                        exactly the propose the picker would have, and the whole
                        repair is one click plus the normal approval. */}
                    {canPropose && !data?.core_owned && cr.state === 'satisfied' && !cr.wired && (
                      <button
                        onClick={() => act(cap.key, 'enable')}
                        disabled={busy !== ''}
                        title={`Propose wiring ${cr.env_var} to the credential that already exists`}
                        className="mt-1 block text-xs px-2 py-0.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
                      >
                        {busy === `enable:${cap.key}` ? 'Proposing…' : 'Fix wiring'}
                      </button>
                    )}
                    {/* WHAT TO DO. A surface that reports a problem and not its
                        resolution makes the operator guess — and this row
                        previously showed two contradictory sentences and no
                        instruction at all. Rendered as a labelled step so it
                        reads as an action rather than more commentary. */}
                    {cr.next_action && (
                      <span className="mt-1 block rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-900">
                        <span className="font-medium">To fix: </span>{cr.next_action}
                      </span>
                    )}
                    {/* The override that read as ready. Shown rather than
                        corrected: it may well be telling the truth — the
                        credential IS set up, under a name the fleet-level field
                        cannot express — so the operator gets the conflict, not a
                        resolution someone else chose.

                        The server suppresses this once THIS agent's credential
                        is verified, so it can no longer contradict a ✓ on the
                        line above. */}
                    {cr.override_masks_fields && (
                      <span className="block text-amber-700">
                        Console reports <span className="font-mono">{cr.prerequisite_key}</span> satisfied via a
                        manual override while a required field is unprovisioned — the override may be
                        correct, but it is not evidence this credential exists.
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* FALLBACK to the fleet-level answer, only when the per-agent read
                is unavailable. Kept rather than dropped: a console outage should
                degrade to the coarser signal, not to silence. The wording says
                "for the fleet" so the coarser scope is never mistaken for this
                agent's own credential. */}
            {(cap.credentials ?? []).length === 0 && cap.requires_prerequisite && (
              <p className="mt-2 text-xs">
                {data && !data.credentials_readable ? (
                  <span className="text-gray-500">
                    Needs credential <span className="font-mono">{cap.requires_prerequisite}</span> —
                    this agent&apos;s own credential status couldn&apos;t be checked
                    {cap.prerequisite_state === 'satisfied' && ', though it is provisioned for the fleet'}
                    {cap.prerequisite_state === 'unsatisfied' && ', and it is not provisioned for the fleet'}
                    .
                  </span>
                ) : (
                  <span className="text-gray-500">
                    Needs credential <span className="font-mono">{cap.requires_prerequisite}</span>
                  </span>
                )}
              </p>
            )}

            {cap.tools.length > 0 && (
              <div className="mt-2">
                <button
                  onClick={() => setToolsOpen(o => ({ ...o, [cap.key]: !o[cap.key] }))}
                  aria-expanded={!!toolsOpen[cap.key]}
                  className="text-xs font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                  <span className="inline-block w-3 text-gray-400">{toolsOpen[cap.key] ? '▾' : '▸'}</span>
                  Tools ({cap.tools.length})
                </button>
                {toolsOpen[cap.key] && (
                  <ul className="space-y-0.5 mt-1 ml-4">
                    {cap.tools.map(t => (
                      <li key={t} className="text-xs font-mono text-gray-600">{t}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Scoping. Values appear only for keys the catalog declares
                non-secret; everything else is a name. Until the config editor
                itself moves (ADR-111 D5.4, amended 2026-08-22 — it stays in
                Console), this section's job is to show the operator what the
                scoping IS and get them to where it is changed in one click.
                Listing bare names with nothing to act on read as a missing
                feature rather than a deliberate boundary. */}
            {cap.config_keys.length > 0 && (
              <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                <p className="font-medium text-gray-600">Scoping &amp; config</p>
                {cap.config_keys.map(k => {
                  const v = cap.config?.[k]
                  return (
                    <p key={k} className="font-mono">
                      {k}
                      {v !== undefined
                        ? <>: <span className="text-gray-700">{v === '' ? <em className="not-italic text-amber-700">not set</em> : v}</span></>
                        : <span className="ml-1 not-italic text-gray-400">(credential — set in Console)</span>}
                    </p>
                  )
                })}
                {consoleURL && fleetSlug && (
                  <a
                    href={`${consoleURL}/fleets/${encodeURIComponent(fleetSlug)}?tab=capabilities&subtab=catalog&mcp=${encodeURIComponent(cap.key)}&configAgent=${encodeURIComponent(agent)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-block mt-1 text-indigo-600 underline not-italic font-sans"
                  >
                    Configure {cap.label} for {agent} in Console →
                  </a>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {consoleURL && (
        <a href={consoleURL} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-indigo-600 underline">
          Manage capabilities &amp; credentials in Console →
        </a>
      )}
    </div>
  )
}

/** Small pill used by the Capabilities facet. Local on purpose: it carries no
 *  state and no behaviour, so a shared component would be indirection without
 *  reuse until a second facet needs the same shape. */
function Badge({ tone, children }: { tone: 'amber' | 'blue' | 'red' | 'gray'; children: React.ReactNode }) {
  const cls = {
    amber: 'bg-amber-100 text-amber-800',
    blue: 'bg-blue-100 text-blue-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-600',
  }[tone]
  return <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${cls}`}>{children}</span>
}

export function FleetEditor() {
  const { user } = useAuth()
  const canPropose = user.fleet_role === 'fleet_owner'
  const params = useParams<{ agent?: string; facet?: string; name?: string }>()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const [roster, setRoster] = useState<string[]>([])
  const [coreAgents, setCoreAgents] = useState<string[]>([])
  const [rosterLoading, setRosterLoading] = useState(true)
  const [agentFilter, setAgentFilter] = useState('')
  const [railCollapsed, setRailCollapsed] = useState(false)  // collapsible Agents picker, like the other panels
  // Core-owned agents sit in their own section under the fleet's own agents,
  // closed by default: few operators ever open one, but they stay one click away.
  const [coreSectionOpen, setCoreSectionOpen] = useState(false)
  const { ref: railRef, width: railWidth, handleProps: railHandleProps } = useResizableWidth({
    storageKey: 'mc.fleetEditor.railWidth',
    min: MIN_RAIL_PX,
    max: MAX_RAIL_PX,
    // gap-4 between the rail and the facet.
    room: () => (railRef.current?.parentElement?.getBoundingClientRect().width ?? Infinity) - MIN_MAIN_PX - 16,
  })
  // ADR-111 D2: badge an agent in the rail when ANY of its overlay files
  // (SOUL.md, crons.yaml, a skill, an activity, ...) is staged on develop
  // but not yet live — the coarsest-grained of this feature's four badges,
  // for an operator scanning the rail rather than a specific facet.
  const { paths: stagedPaths } = useStagedPaths()
  // A propose the operator walked away from mid-flight. It is held HERE, not in
  // the form: the form unmounts the moment the create area is left, while this
  // shell renders every route in the section and so outlives the request. See
  // NewAgentProposeStrip.
  const [pendingPropose, setPendingPropose] = useState<PendingPropose | null>(null)

  const loadRoster = () => {
    setRosterLoading(true)
    Promise.all([api.listAgents(), api.listCoreAgents()])
      .then(([a, c]) => { setRoster((a.agents ?? []).slice().sort()); setCoreAgents(c.agents ?? []) })
      .catch(() => {})
      .finally(() => setRosterLoading(false))
  }
  useEffect(loadRoster, [])

  // The agentless areas (ADR-111 IA): Resources, Data, and creating an agent.
  // None of them has an owning agent, so none shows facet tabs. Creating an
  // agent belongs here rather than in a panel layered over whichever agent
  // happened to be selected — that layering put two agents' authoring surfaces
  // on screen at once, with the lower one's buttons still live.
  const area = fleetAreaFor(location.pathname)
  const onFleetArea = area !== null

  // Where ✕ close returns to. The opener passes its own path+query in router
  // state (the query carries facet state: ?edit=1 on a workflow, ?path= on a
  // file). Anything else falls back to /fleet-editor, which redirects to the
  // first agent's Workflows: no state at all (a direct link, a refresh, a fresh
  // tab), a self-referential origin (the rail entry clicked while already here,
  // which would leave close navigating to where it already is), or a value that
  // is not a Fleet Editor path — history state is caller-supplied, so it is
  // validated rather than fed straight to navigate().
  //
  // The section root /fleet-editor is a legitimate origin, not just a prefix of
  // one: the landing redirect below only fires once the roster has agents in
  // it, so an empty roster (first run) or a failed listAgents parks the operator
  // on the empty-state pane whose copy points straight at ＋ New agent.
  const cameFrom = (location.state as { from?: string } | null)?.from
  const cameFromPath = cameFrom?.split('?')[0]
  const originFrom =
    cameFromPath &&
    (cameFromPath === '/fleet-editor' || cameFromPath.startsWith('/fleet-editor/')) &&
    fleetAreaFor(cameFromPath) !== 'new-agent'
      ? cameFrom
      : null
  // When there IS a recorded origin it is also the previous history entry —
  // every way in either pushes from it (＋ / rail entry / deep link with state)
  // or, when clicked from here, replaces the create entry while carrying the
  // same origin forward. So close steps BACK to it instead of navigating to it:
  // a fresh navigate(replace) would leave two adjacent identical entries and
  // the operator's first browser Back after closing would do nothing visible.
  // With no origin (a direct link, a refresh, a fresh tab) there is nothing to
  // step back to, so replace the create entry with the section root, which
  // redirects on to the first agent's Workflows.
  const closeNewAgent = () => {
    if (originFrom) navigate(-1)
    else navigate('/fleet-editor', { replace: true })
  }
  // What the two "＋ New agent" entries record as the origin. Clicking either
  // while ALREADY on the create area must not overwrite it with the create path
  // itself — that origin is self-referential, so close would fall back to the
  // section root and lose the facet the operator actually came from.
  // Forwarding the VALIDATED origin, not the raw state: a value that failed the
  // check above is not made good by being carried one entry further.
  const newAgentFrom = area === 'new-agent' ? originFrom : location.pathname + location.search
  const agent = onFleetArea ? undefined : params.agent
  // Both the facet route and the entity route carry `:facet`, so the focused
  // entity in `:name` never has to be guessed from the facet's identity.
  const facet: Facet | undefined = onFleetArea ? undefined : (params.facet as Facet | undefined)

  const fleetRoster = roster.filter(a => !coreAgents.includes(a))
  const coreRoster = roster.filter(a => coreAgents.includes(a))

  // Default landing (IA-6): no agent selected and not on the Files escape
  // hatch — send the operator to the first agent's Workflows facet. The fleet's
  // own agents come first; a core agent is the landing only when there is
  // nothing else, since its section starts closed.
  useEffect(() => {
    if (onFleetArea || rosterLoading || params.agent) return
    const first = roster.find(a => !coreAgents.includes(a)) ?? roster[0]
    if (first) {
      navigate(`/fleet-editor/agents/${encodeURIComponent(first)}/workflows`, { replace: true })
    }
  }, [onFleetArea, rosterLoading, roster, coreAgents, params.agent, navigate])

  const isCoreOwned = agent ? coreAgents.includes(agent) : false
  // A closed section must not hide the agent being edited: a deep link or a
  // breadcrumb onto a core agent opens it so the selection shows in the rail.
  useEffect(() => {
    if (isCoreOwned) setCoreSectionOpen(true)
  }, [isCoreOwned])
  const matchesFilter = (a: string) => a.toLowerCase().includes(agentFilter.trim().toLowerCase())
  const filteredRoster = roster.filter(matchesFilter)
  const filteredFleetRoster = fleetRoster.filter(matchesFilter)
  const filteredCoreRoster = coreRoster.filter(matchesFilter)
  // Typing a filter is a search, so its core matches show without a click.
  const coreListShown = coreSectionOpen || agentFilter.trim() !== ''
  const brain = agent ? isBrain(agent) : false
  const visibleFacets = brain ? [...BASE_FACETS, { id: 'routing' as Facet, label: 'Routing' }] : BASE_FACETS

  // The facet tabs ride in the AppShell header rather than on a row of their
  // own under it. That row cost ~50px on every facet while the header beside
  // the breadcrumb sat half empty — real estate the authoring surfaces below
  // actually need. Nothing is hidden or demoted: the same tabs, one row up.
  //
  // They go through OverflowNav rather than a bare <nav> because the header is
  // one row with a hard width: at half-screen the eight-facet brain strip used
  // to paint over the account chip (Asana 1217649894859355). OverflowNav
  // measures the space the shell gave the slot and moves whatever no longer
  // fits into a More disclosure, so every facet stays a reachable link at every
  // width. The active facet is never the one hidden.
  //
  // `className` is passed beside `label` because OverflowNav measures a hidden
  // `span` rather than the live Link — a mirror without the tab's padding and
  // font size measures short, which under-hides and lets the row clip. The shell
  // still never inspects the slot: this whole node stays an opaque ReactNode.
  useHeaderSlot(
    agent && !onFleetArea ? (
      <OverflowNav
        ariaLabel="Agent facets"
        items={visibleFacets.map(f => {
          const active = facet === f.id
          const className = `${FACET_TAB_CLASS} ${active ? FACET_TAB_ACTIVE_CLASS : FACET_TAB_IDLE_CLASS}`
          return {
            key: f.id,
            label: f.label,
            active,
            className,
            node: (
              <Link
                to={`/fleet-editor/agents/${encodeURIComponent(agent)}/${f.id}`}
                aria-current={active ? 'page' : undefined}
                className={className}
              >
                {f.label}
              </Link>
            ),
          }
        })}
      />
    ) : null,
    // The brain's extra Routing facet reaches the strip through
    // `visibleFacets.length`: `isBrain(agent)` is a function of `agent`, so the
    // list changes only when one of these does.
    [agent, facet, onFleetArea, visibleFacets.length],
  )

  const renderRailAgent = (a: string) => (
    <li key={a}>
      <Link
        // Preserve the currently-open facet when switching agents (IA-2)
        // instead of snapping back to Workflows — see railFacetFor.
        to={`/fleet-editor/agents/${encodeURIComponent(a)}/${railFacetFor(facet, a)}`}
        aria-current={!onFleetArea && agent === a}
        // The full name on hover: it truncates beside the badges.
        title={a}
        className={`flex items-center justify-between gap-1 px-3 py-1.5 text-sm truncate ${
          !onFleetArea && agent === a ? 'bg-indigo-50 text-indigo-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        <span className="truncate" data-fit-name>{a}</span>
        <span className="shrink-0 flex items-center gap-1">
          {isPrefixStaged(stagedPaths, `agents/${a}/`) && <StagedBadge />}
        </span>
      </Link>
    </li>
  )

  return (
    <div className="h-full flex gap-4">
      {/* Agents rail (IA-1) — filterable + collapsible */}
      {railCollapsed ? (
        <div className="w-10 shrink-0 flex flex-col items-center rounded-lg border border-gray-200 bg-white pt-2">
          <button type="button" onClick={() => setRailCollapsed(false)} title="Show agents"
            aria-label="Show agents" className="text-gray-500 hover:text-gray-800">›</button>
          <span className="mt-2 text-[10px] text-gray-400" style={{ writingMode: 'vertical-rl' }}>Agents</span>
        </div>
      ) : (
      <div
        ref={railRef}
        data-testid="agents-rail"
        style={{ width: railWidth ?? DEFAULT_RAIL_PX }}
        className="shrink-0 flex flex-col rounded-lg border border-gray-200 bg-white relative"
      >
        {/* The rail's right edge, in the gutter — so no overflow-hidden on the
            rail itself, which would clip it; the sections below clip instead. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the agents list"
          tabIndex={0}
          {...railHandleProps}
          data-testid="agents-rail-resizer"
          className="absolute right-0 top-0 bottom-0 -mr-2 w-2 cursor-col-resize z-10 group
                     flex items-center justify-center hover:bg-indigo-100 focus:bg-indigo-100 focus:outline-none"
          title="Drag to resize — double-click to fit the longest name, or use the arrow keys"
        >
          <span className="w-0.5 h-8 rounded-full bg-gray-300 group-hover:bg-indigo-400" />
        </div>
        <div className="p-2 border-b border-gray-100 flex items-center justify-between gap-1">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Agents</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(NEW_AGENT_PATH, { state: { from: newAgentFrom }, replace: area === 'new-agent' })}
              className="text-sm text-emerald-600 hover:text-emerald-800 leading-none"
              title="Author a brand-new agent"
            >
              ＋
            </button>
            <button type="button" onClick={() => setRailCollapsed(true)} title="Hide agents"
              aria-label="Hide agents" className="text-gray-400 hover:text-gray-700 leading-none">‹</button>
          </div>
        </div>
        {/* Creating an agent belongs to the Agents panel — it acts on the roster
            right below it — not to the fleet-level areas pinned at the bottom,
            which have no owning agent at all. It still reads as current while
            you author, so no agent looks selected underneath. */}
        <div className="border-b border-gray-100">
          <Link
            to={NEW_AGENT_PATH}
            state={{ from: newAgentFrom }}
            // Explicit, matching the header ＋ above: entering pushes, so close
            // has a previous entry to step back to; re-entering from here
            // replaces, so the create area never stacks on itself. Link's own
            // default would only replace when the whole path+query matches.
            replace={area === 'new-agent'}
            aria-current={area === 'new-agent'}
            className={`block px-3 py-2 text-sm ${area === 'new-agent' ? 'bg-indigo-50 text-indigo-800 font-medium' : 'text-emerald-700 hover:bg-gray-50'}`}
            title="Author a brand-new agent — proposed for review, live once approved and deployed"
          >
            ＋ New agent
          </Link>
        </div>
        <div className="p-2 border-b border-gray-100">
          <input type="text" value={agentFilter} onChange={e => setAgentFilter(e.target.value)}
            placeholder="Filter agents…" className="w-full text-sm border border-gray-300 rounded px-2 py-1" />
        </div>
        <div className="flex-1 min-h-0 overflow-auto">
          {rosterLoading ? (
            <p className="text-xs text-gray-400 p-2 animate-pulse">Loading…</p>
          ) : roster.length === 0 ? (
            <p className="text-xs text-gray-500 p-2">No agents yet.</p>
          ) : filteredRoster.length === 0 ? (
            <p className="text-xs text-gray-500 p-2">No agents match.</p>
          ) : (
            <>
              {filteredFleetRoster.length > 0 ? (
                <ul>{filteredFleetRoster.map(a => renderRailAgent(a))}</ul>
              ) : agentFilter.trim() === '' ? (
                <p className="text-xs text-gray-500 p-2">No fleet agents yet.</p>
              ) : null}
              {filteredCoreRoster.length > 0 && (
                <div data-testid="core-agents-section" className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setCoreSectionOpen(open => !open)}
                    aria-expanded={coreListShown}
                    className="w-full flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:bg-gray-50"
                    title="Agents provided by core (ADR-103) — read-only here"
                  >
                    <span aria-hidden className="w-3">{coreListShown ? '▾' : '▸'}</span>
                    Core Agents
                    <span className="font-normal normal-case">({filteredCoreRoster.length})</span>
                  </button>
                  {coreListShown && <ul>{filteredCoreRoster.map(a => renderRailAgent(a))}</ul>}
                </div>
              )}
            </>
          )}
        </div>
        <div className="border-t border-gray-100 rounded-b-lg overflow-hidden">
          <Link
            to={PLATFORM_SKILLS_PATH}
            aria-current={area === 'platform-skills' ? 'page' : undefined}
            className={`block px-3 py-2 text-sm ${area === 'platform-skills' ? 'bg-indigo-50 text-indigo-800 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}
            title="Universal skills from agentcore, composed onto agents at deploy (ADR-011). Read-only."
          >
            Platform skills
          </Link>
          <Link
            to="/fleet-editor/resources"
            aria-current={area === 'resources'}
            className={`block px-3 py-2 text-sm ${area === 'resources' ? 'bg-indigo-50 text-indigo-800 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}
            title="The fleet's shared knowledge base (repo-root resources/) — reference material any agent can draw on (ADR-111 IA-3)"
          >
            Resources
          </Link>
          <Link
            to="/fleet-editor/data"
            aria-current={area === 'data'}
            className={`block px-3 py-2 text-sm ${area === 'data' ? 'bg-indigo-50 text-indigo-800 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}
            title="Versioned reference data (config/schemas + config/data) — the schemas and data sets skills and workflows reference instead of hardcoding facts (ADR-125/127)"
          >
            Data
          </Link>
        </div>
      </div>
      )}

      {/* Main: exactly ONE surface — an agentless area or the selected agent's facet */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {pendingPropose && (
          // Keyed: a second handed-off propose must not reuse the first one's
          // rendered receipt. See NewAgentProposeStrip, which also resets on a
          // prop change for the same-name case this key cannot catch.
          <NewAgentProposeStrip key={pendingPropose.profile} pending={pendingPropose}
            onDismiss={() => setPendingPropose(null)} />
        )}
        {area === 'new-agent' ? (
          // Its own scroll container: the facet branch below brings one, and
          // Resources/Data scroll internally, but the form has none of its own.
          <div className="flex-1 min-h-0 overflow-auto">
            {/*
              location.key changes on every navigation INTO this area, including
              a re-entry from here (both "＋ New agent" entries navigate to the
              route that is already active, so React Router re-renders in place
              and a finished propose's terminal panel would otherwise survive,
              leaving both entries dead). It is passed as a PROP, not as `key`:
              remounting would also wipe a half-typed draft and orphan an
              in-flight propose. See NewAgentForm.
            */}
            <NewAgentForm entryKey={location.key} canPropose={canPropose} onClose={closeNewAgent}
              onLeaveWhileBusy={setPendingPropose} pendingProfile={pendingPropose?.profile ?? null} />
          </div>
        ) : area === 'platform-skills' ? (
          <PlatformSkillsArea selectedName={platformSkillFromPath(location.pathname)} />
        ) : area === 'resources' ? (
          <FilesStudio canPropose={canPropose} />
        ) : area === 'data' ? (
          // The set ref is the rest of the path after /fleet-editor/data/ — a
          // splat, not a param, because a ref carries `@v<N>` and may carry the
          // reserved `org/` namespace, so it is not one segment.
          <DataArea canPropose={canPropose} initialSet={dataSetFromPath(location.pathname)} />
        ) : !agent ? (
          <p className="text-sm text-gray-400 p-6">
            {rosterLoading ? 'Loading…' : 'Select an agent, or ＋ New agent to author one.'}
          </p>
        ) : (
          <>
            {/*
              key={agent} remounts the active facet whenever the focused agent
              changes. React Router keeps the same route element across an
              :agent param change, so a facet whose data-load isn't keyed on
              `agent` (Skills/Crons/Persona/Capabilities/Profile all hold
              per-agent state seeded at mount) would otherwise keep showing the
              previous agent's content. Remounting reloads the new agent's data
              and resets any in-facet selection — the correct behavior when the
              operator switches agents while staying on a facet (retained by
              railFacetFor). Workflows already re-fetched on `agent`; remount is
              harmless there.
            */}
            <div key={agent} className="flex-1 min-h-0 overflow-auto">
              {facet === 'persona' && <PersonaFacet agent={agent} canPropose={canPropose} />}
              {facet === 'skills' && (
                <SkillStudio
                  canPropose={canPropose}
                  scopedAgent={agent}
                  initialName={params.name}
                  onOpenWorkflow={(a, name, edit) =>
                    navigate(`/fleet-editor/agents/${encodeURIComponent(a)}/workflows/${encodeURIComponent(name)}${edit ? '?edit=1' : ''}`)
                  }
                />
              )}
              {facet === 'workflows' && (
                <WorkflowsFacet agent={agent} initialName={params.name} initialEdit={searchParams.get('edit') === '1'} />
              )}
              {facet === 'crons' && <CronStudio canPropose={canPropose} scopedAgent={agent} isCoreOwned={isCoreOwned} initialName={params.name} />}
              {facet === 'capabilities' && <CapabilitiesFacet agent={agent} canPropose={canPropose} />}
              {facet === 'profile' && <AgentProfileFacet agent={agent} canPropose={canPropose} isCoreOwned={isCoreOwned} />}
              {facet === 'routing' && (brain ? <RoutingFacet agent={agent} canPropose={canPropose} /> : (
                <p className="text-sm text-gray-500 p-6">Routing is only available on the brain agent.</p>
              ))}
              {/* ADR-111 IA-7 — the same FilesStudio the Resources area uses,
                  scoped to this agent's overlay. A core-owned agent's files are
                  read-only (ADR-103), enforced server-side per path and mirrored
                  here so the create buttons are not offered at all. */}
              {facet === 'files' && (
                <FilesStudio
                  canPropose={canPropose && !isCoreOwned}
                  scope={agentFilesScope(agent)}
                  // `?path=` opens one file directly — what the workflow
                  // editor's python `file:` link targets.
                  initialPath={searchParams.get('path') ?? undefined}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/** Compat redirect for the pre-amendment D2 deep-link shape
 * (/fleet-editor/:agent/:name) into the new agent-centric one. D2 was only
 * deployed to orion for review (not GA), so this is a courtesy for any
 * bookmark made during that window, not a long-term contract. */
export function LegacyWorkflowRedirect() {
  const { agent, name } = useParams<{ agent: string; name: string }>()
  if (!agent || !name) return <Navigate to="/fleet-editor" replace />
  return <Navigate to={`/fleet-editor/agents/${encodeURIComponent(agent)}/workflows/${encodeURIComponent(name)}`} replace />
}
